import type { HubSectionTitle, ResourceCategory, WebResource } from '@/content/resources';
import { categoryOrder } from '@/content/resources';

export const POPULAR_SECTION_TITLE = 'Popular';
export const WHATS_NEW_SECTION_TITLE = "What's New";
export const PREMIUM_SECTION_TITLE = 'Premium';
export const LEARNING_HUB_NEW_BADGE_TEXT = 'NEW';
export const LEARNING_HUB_POPULAR_BADGE_TEXT = 'POPULAR';
export const LOCAL_RESOURCE_NEW_DAYS = 30;

export type HubSortOption = 'alphabetical' | 'popularity' | 'newest';

export type HubSection =
  | { kind: 'popular'; title: typeof POPULAR_SECTION_TITLE }
  | { kind: 'whats-new'; title: typeof WHATS_NEW_SECTION_TITLE }
  | { kind: 'premium'; title: typeof PREMIUM_SECTION_TITLE }
  | { kind: 'category'; title: ResourceCategory };

function parseDateMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

export function isResourceNew(resource: WebResource): boolean {
  if (resource.badgeText === LEARNING_HUB_NEW_BADGE_TEXT) return true;
  const added = parseDateMs(resource.addedOn);
  if (added == null) return false;
  return added >= Date.now() - LOCAL_RESOURCE_NEW_DAYS * 24 * 60 * 60 * 1000;
}

export function resourceNewestAtMs(resource: WebResource): number | null {
  return parseDateMs(resource.publishedAt) ?? parseDateMs(resource.addedOn);
}

export function sortAlphabetically(a: WebResource, b: WebResource) {
  return a.title.localeCompare(b.title);
}

export function sortByNewest(a: WebResource, b: WebResource) {
  const aMs = resourceNewestAtMs(a);
  const bMs = resourceNewestAtMs(b);
  if (aMs != null && bMs != null && aMs !== bMs) return bMs - aMs;
  if (aMs != null && bMs == null) return -1;
  if (aMs == null && bMs != null) return 1;
  return sortAlphabetically(a, b);
}

export function sortOptionsForSection(title: string): { value: HubSortOption; label: string }[] {
  if (title === WHATS_NEW_SECTION_TITLE) {
    return [
      { value: 'newest', label: 'Newest' },
      { value: 'alphabetical', label: 'A–Z' },
    ];
  }
  return [
    { value: 'popularity', label: 'Popularity' },
    { value: 'alphabetical', label: 'A–Z' },
  ];
}

export function defaultSortForSection(title: string): HubSortOption {
  return title === WHATS_NEW_SECTION_TITLE ? 'newest' : 'popularity';
}

export function sortSectionResources(
  items: WebResource[],
  sort: HubSortOption,
  popularityIndex: Map<string, number>,
): WebResource[] {
  const copy = items.slice();
  if (sort === 'newest') return copy.sort(sortByNewest);
  if (sort === 'alphabetical') return copy.sort(sortAlphabetically);
  return copy.sort((a, b) => {
    const aRank = popularityIndex.get(a.href) ?? Number.POSITIVE_INFINITY;
    const bRank = popularityIndex.get(b.href) ?? Number.POSITIVE_INFINITY;
    if (aRank !== bRank) return aRank - bRank;
    return sortAlphabetically(a, b);
  });
}

export function hubSectionFromSlug(slug: string | undefined): HubSection | null {
  if (!slug) return null;
  const normalized = slug.toLowerCase();
  if (normalized === 'popular') return { kind: 'popular', title: POPULAR_SECTION_TITLE };
  if (normalized === 'whats-new' || normalized === 'whats_new') {
    return { kind: 'whats-new', title: WHATS_NEW_SECTION_TITLE };
  }
  if (normalized === 'premium') return { kind: 'premium', title: PREMIUM_SECTION_TITLE };
  const category = categoryOrder.find((item) => item.toLowerCase() === normalized);
  return category ? { kind: 'category', title: category } : null;
}

export function categorySlug(title: HubSectionTitle): string {
  if (title === WHATS_NEW_SECTION_TITLE) return 'whats-new';
  return title.toLowerCase();
}

export function collapseSplitResources(resources: WebResource[]): WebResource[] {
  const splitSlugs = new Set(resources.filter((item) => item.href.includes('?')).map((item) => item.slug));
  return resources.filter((item) => !splitSlugs.has(item.slug) || item.href.includes('?'));
}

export function resourcesForSection(
  section: HubSection,
  resources: WebResource[],
  popular: WebResource[],
): WebResource[] {
  if (section.kind === 'popular') return popular;
  if (section.kind === 'whats-new') return resources.filter(isResourceNew).sort(sortByNewest);
  if (section.kind === 'premium') return resources.filter((item) => item.premium);
  return resources.filter((item) => item.category === section.title);
}

export function tileBadgeForSection(
  sectionTitle: string,
  resource: WebResource,
  popularFromApi: boolean,
): string | undefined {
  if (sectionTitle === POPULAR_SECTION_TITLE && popularFromApi) return LEARNING_HUB_POPULAR_BADGE_TEXT;
  if (isResourceNew(resource)) return LEARNING_HUB_NEW_BADGE_TEXT;
  return resource.badgeText ?? undefined;
}
